import type { ConsultationRequestDTO, ScheduledConsultationDTO } from '@/generated'
import type { ConsultationRequestFilter } from '@/stores/models/ConsultationRequestFilter'

export function filterDirectConsultationByHospital(
  consultation: ConsultationRequestDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { hospitals } = filter

  if (!hospitals?.length) {
    return true
  }

  return hospitals.some((h) => h.id === consultation.case.hospital.id)
}

export function filterDirectConsultationByMedicalField(
  consultation: ConsultationRequestDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { targetMedicalFields } = filter

  if (!targetMedicalFields?.length) {
    return true
  }

  return targetMedicalFields.some((f) => f === consultation.medicalField)
}

export function filterDirectConsultationByPatientDepartment(
  consultation: ConsultationRequestDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { departments } = filter

  if (!departments?.length) {
    return true
  }

  return departments.some((d) => d === consultation.case.department.name)
}

export function filterDirectConsultationByUrgency(
  consultation: ConsultationRequestDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { urgencies } = filter

  if (!urgencies?.length) {
    return true
  }

  return urgencies.some((u) => u === consultation.urgency)
}

export function filterDirectConsultationByAssignee(
  consultation: ConsultationRequestDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { assignees } = filter

  if (!assignees?.length) {
    return true
  }

  return assignees.some((a) => a.userName === consultation.assignedTo?.userName)
}

export function filterDirectConsultationByRequester(
  consultation: ConsultationRequestDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { requesters } = filter

  if (!requesters?.length) {
    return true
  }

  return requesters.some((r) => r.userName === consultation.requestedBy.user.userName)
}

export function filterScheduledConsultationByHospital(
  consultation: ScheduledConsultationDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { hospitals } = filter

  if (!hospitals?.length) {
    return true
  }

  return consultation.cases.some((c) => hospitals.some((h) => h.id === c.hospital.id))
}

export function filterScheduledConsultationByMedicalField(
  consultation: ScheduledConsultationDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { targetMedicalFields } = filter

  if (!targetMedicalFields?.length) {
    return true
  }

  return consultation.participants.some((participant) =>
    participant.medicalFields?.some((field) => targetMedicalFields.includes(field))
  )
}

export function filterScheduledConsultationByPatientDepartment(
  consultation: ScheduledConsultationDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { departments } = filter

  if (!departments?.length) {
    return true
  }

  return consultation.cases.some((c) => departments.some((d) => d === c.department.name))
}

export function filterScheduledConsultationByUrgency(
  _consultation: ScheduledConsultationDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { urgencies } = filter

  if (!urgencies?.length) {
    return true
  }

  return false
}

export function filterScheduledConsultationByAssignee(
  consultation: ScheduledConsultationDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { assignees } = filter

  if (!assignees?.length) {
    return true
  }

  return consultation.participants.some((participant) =>
    assignees.some((a) => a.userName === participant.user.userName)
  )
}

export function filterScheduledConsultationByRequester(
  consultation: ScheduledConsultationDTO,
  filter: ConsultationRequestFilter
): boolean {
  const { requesters } = filter

  if (!requesters?.length) {
    return true
  }

  return requesters.some((r) => r.userName === consultation.requestedBy.user.userName)
}

export function filterDirectConsultationRequests(
  consultation: ConsultationRequestDTO,
  filter: ConsultationRequestFilter,
  hasChiefPhysicianShift: boolean
): boolean {
  const hasActiveFilters = !!(
    filter.hospitals?.length ||
    filter.targetMedicalFields?.length ||
    filter.departments?.length ||
    filter.urgencies?.length ||
    filter.assignees?.length ||
    filter.requesters?.length
  )

  if (!hasChiefPhysicianShift && !hasActiveFilters) {
    return true
  }

  if (!hasChiefPhysicianShift && hasActiveFilters) {
    const filters = [
      filterDirectConsultationByHospital,
      filterDirectConsultationByMedicalField,
      filterDirectConsultationByPatientDepartment,
      filterDirectConsultationByUrgency,
      filterDirectConsultationByAssignee,
      filterDirectConsultationByRequester
    ]

    return filters.every((filterFn) => filterFn(consultation, filter))
  }

  const filters = [
    filterDirectConsultationByHospital,
    filterDirectConsultationByMedicalField,
    filterDirectConsultationByPatientDepartment,
    filterDirectConsultationByUrgency,
    filterDirectConsultationByAssignee,
    filterDirectConsultationByRequester
  ]

  return filters.every((filterFn) => filterFn(consultation, filter))
}

export function filterScheduledConsultationRequests(
  consultation: ScheduledConsultationDTO,
  filter: ConsultationRequestFilter,
  _hasChiefPhysicianShift: boolean
): boolean {
  const hasActiveFilters = !!(
    filter.hospitals?.length ||
    filter.targetMedicalFields?.length ||
    filter.departments?.length ||
    filter.urgencies?.length ||
    filter.assignees?.length ||
    filter.requesters?.length
  )

  if (!hasActiveFilters) {
    return true
  }

  const filters = [
    filterScheduledConsultationByHospital,
    filterScheduledConsultationByMedicalField,
    filterScheduledConsultationByPatientDepartment,
    filterScheduledConsultationByUrgency,
    filterScheduledConsultationByAssignee,
    filterScheduledConsultationByRequester
  ]

  return filters.every((filterFn) => filterFn(consultation, filter))
}
